import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../lib/api';
import type { EnrollmentWithSubject, TermBillingResult, ChargeAbsentBulkResult } from '@repo/shared-types';
import type { CreateEnrollmentInput, UpdateEnrollmentInput, ChargeAbsentBulkInput } from '@repo/validation';
import { toast } from 'sonner';
import i18n from '../i18n';
import { invalidateGettingStarted } from './useGettingStarted';

export function useEnrollments(studentId: string | null) {
  return useQuery<EnrollmentWithSubject[]>({
    queryKey: ['enrollments', { studentId }],
    queryFn: () => fetchApi(`/enrollments?studentId=${studentId!}`),
    enabled: studentId !== null,
  });
}

/**
 * Every enrollment in the org — GET /enrollments with no `studentId`, which
 * the route already answers with the full list.
 *
 * One hook so the three callers that need it (the workbench's attention card,
 * the one-off session dialog, the billing-rules apply-to-all split) share the
 * `['enrollments','all']` cache entry instead of each writing the same query
 * inline and drifting. `enabled` keeps it off the wire for callers gated on a
 * permission or on a dialog nobody has opened yet.
 */
export function useAllEnrollments(enabled = true) {
  return useQuery<EnrollmentWithSubject[]>({
    queryKey: ['enrollments', 'all'],
    queryFn: () => fetchApi<EnrollmentWithSubject[]>('/enrollments'),
    enabled,
  });
}

export function useCreateEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEnrollmentInput) =>
      fetchApi<EnrollmentWithSubject>('/enrollments', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['enrollments', { studentId: vars.studentId }] });
      invalidateGettingStarted(qc); // checklist step 4, "enrolled"
      toast.success('Đã thêm đăng ký học');
    },
    onError: (e) => toast.error(e.message || 'Không thể thêm đăng ký'),
  });
}

export function useUpdateEnrollment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateEnrollmentInput) =>
      fetchApi<EnrollmentWithSubject>(`/enrollments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => {
      // Status changes (pause/resume/end) also remove/repopulate future session participants server-side.
      qc.invalidateQueries({ queryKey: ['enrollments'] });
      qc.invalidateQueries({ queryKey: ['sessions'] });
      // These write ENROLLMENT_* audit rows shown in the student's "Lịch sử thao tác".
      qc.invalidateQueries({ queryKey: ['students'] });
    },
    onError: (e) => toast.error(e.message || 'Không thể cập nhật đăng ký'),
  });
}

export function useDeleteEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<{ deleted: boolean }>(`/enrollments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments'] });
      qc.invalidateQueries({ queryKey: ['students'] }); // refresh the student's audit trail
      toast.success('Đã xoá đăng ký');
    },
    onError: (e) => toast.error(e.message || 'Không thể xoá đăng ký'),
  });
}

function invalidateAfterTermBilling(qc: ReturnType<typeof useQueryClient>, studentId?: string) {
  qc.invalidateQueries({ queryKey: ['enrollments'] });
  qc.invalidateQueries({ queryKey: ['invoices'] });
  if (studentId) qc.invalidateQueries({ queryKey: ['students', studentId] });
}

/** POST /enrollments/:id/term/start — prepay the first/next TERM period (M-D.2). */
export function useStartTerm(enrollmentId: string, studentId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (startDate?: string) =>
      fetchApi<TermBillingResult>(`/enrollments/${enrollmentId}/term/start`, {
        method: 'POST',
        body: JSON.stringify(startDate ? { startDate } : {}),
      }),
    onSuccess: (result) => {
      invalidateAfterTermBilling(qc, studentId);
      toast.success(`Đã bắt đầu ${result.periodLabel} — hoá đơn ${result.invoiceCode}`);
    },
    onError: (e) => toast.error(e.message || 'Không thể bắt đầu khóa'),
  });
}

/** POST /enrollments/:id/term/renew — reconcile the OPEN TERM period + prepay the next one (M-D.2). */
export function useRenewTerm(enrollmentId: string, studentId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchApi<TermBillingResult>(`/enrollments/${enrollmentId}/term/renew`, { method: 'POST' }),
    onSuccess: (result) => {
      invalidateAfterTermBilling(qc, studentId);
      toast.success(`Đã gia hạn ${result.periodLabel} — hoá đơn ${result.invoiceCode}`);
    },
    onError: (e) => toast.error(e.message || 'Không thể gia hạn khóa'),
  });
}

/**
 * Org-wide PER_SESSION enrollments (ACTIVE + PAUSED), fetched only while
 * `enabled` — meant for the "apply to all" confirm dialog in BillingRulesSection,
 * which needs the monthly/term split *before* the bulk mutation runs (POST
 * /enrollments/charge-absent/bulk only returns it after acting). There is no
 * dedicated preview endpoint for this, and Task 2 is scoped to not add one, so
 * this reuses the existing GET /enrollments list (already returns every
 * enrollment when called without `studentId`) and computes the split
 * client-side with the same where-clause the bulk route uses server-side.
 */
export function useChargeAbsentSplit(enabled: boolean) {
  const query = useAllEnrollments(enabled);
  const split = query.data?.reduce(
    (acc, e) => {
      if (e.pricingType !== 'PER_SESSION' || (e.status !== 'ACTIVE' && e.status !== 'PAUSED')) return acc;
      if (e.billingPeriod === 'TERM') acc.term += 1;
      else acc.monthly += 1;
      return acc;
    },
    { monthly: 0, term: 0 },
  );
  return { ...query, split };
}

/** POST /enrollments/charge-absent/bulk — re-apply the org's absence-billing rule to
 *  every existing PER_SESSION enrollment at once (Task 1's API).
 *
 *  `silent` suppresses the SUCCESS toast only (invalidation and the error toast
 *  always run), mirroring `useUpdatePreferences`. The sweep is the tail of one
 *  teacher action — save the default, then apply it — and that whole action
 *  gets exactly ONE toast, owned by the caller so it can report the count the
 *  bulk result returns ("Đã lưu và áp dụng cho N đăng ký", spec §4).
 *  No caller takes the non-silent branch today — it stays the default so a
 *  future standalone caller gets feedback without having to opt in. */
export function useChargeAbsentBulk(options?: { silent?: boolean }) {
  const qc = useQueryClient();
  const silent = options?.silent ?? false;
  return useMutation({
    mutationFn: (input: ChargeAbsentBulkInput) =>
      fetchApi<ChargeAbsentBulkResult>('/enrollments/charge-absent/bulk', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['enrollments'] });
      if (!silent) {
        toast.success(i18n.t('preferences.chargeAbsentApplyDone', { ns: 'settings', count: result.updated }));
      }
    },
    onError: (err: Error) =>
      toast.error(err.message || i18n.t('preferences.toastError', { ns: 'settings' })),
  });
}
